<template>
  <AssigneeSelect
    :default-value
    :disabled="isDisabled"
    :exclude-user-ids
    :focus-on-mount
    :label
    :minimal
    :model-value="assignee"
    no-wrap
    :placeholder
    :size
    @update:model-value="assign"
  />
</template>

<script setup lang="ts">
import type { VInputSize } from '@vartion/ui'
import { type Assignee } from '~/helpers/index.ts'
import { canAssignCase } from '~/stores/user.ts'
import { type CaseModel, type FormattedCaseModel } from '~/types.ts'

interface Props {
  modelValue?: Assignee
  caseModel?: CaseModel | FormattedCaseModel
  placeholder?: string
  label?: string
  excludeUserIds?: Set<number>
  disabled?: boolean
  minimal?: boolean
  hideUserGroups?: boolean
  focusOnMount?: boolean
  defaultValue?: 'self' | 'unassigned' | null
  size?: VInputSize
}

const {
  modelValue = undefined,
  caseModel = undefined,
  placeholder = $t('unassigned'),
  label = undefined,
  excludeUserIds = new Set(),
  disabled = undefined,
  minimal = undefined,
  focusOnMount = undefined,
  defaultValue = 'unassigned',
  size = undefined,
} = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [Assignee]
  changed: []
}>()

const assignee = computed((): Assignee => {
  if (caseModel) {
    return toAssignee(caseModel)
  }

  return modelValue ?? { type: 'group', value: null }
})

const isDisabled = computed(() => {
  if (disabled) return true
  if (!caseModel) return false

  return !canAssignCase(caseModel)
})

async function assign(assignee: Assignee) {
  if (!caseModel) {
    emit('update:modelValue', assignee)

    return
  }

  const updatedCase = await stores.cases.update(caseModel.id, {
    user_id: assignee.type === 'user' ? assignee.value : null,
    group_id: assignee.type === 'group' ? assignee.value : null,
  })
  Object.assign(caseModel, updatedCase)
  emit('changed')
}
</script>

<style scoped>
.v-select :deep() .v-input__inner:disabled + .v-input__suffix {
  display: none;
}
</style>
